/* iter-qa-dashboard · QA reviewer inbox.
 *
 * Route `/qa` — visible to any user whose global role is `editor`
 * (the QA account uses this role). Combines TWO queues in one view:
 *
 *   1. Human-editing requests from writers (`editing_requests`) —
 *      surfaced via GET /api/editing/editor/assignments.
 *   2. Loom manuscripts currently drafting or awaiting proofread —
 *      surfaced via GET /api/loom/editor-inbox.
 *
 * Each row shows writer / project meta, current status, and a jump
 * button into the editor for that project.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PanelHeader } from './admin/_shared';
import { BookOpen, ClipboardList, Loader2, Mail, RefreshCw } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const ax  = axios.create({ baseURL: API, withCredentials: true });

const BUCKET_META = {
  contemp: { label: 'Contemp',  tone: 'bg-rose-100 text-rose-900' },
  dark:    { label: 'Dark',     tone: 'bg-red-100 text-red-900'   },
  grey:    { label: 'Grey',     tone: 'bg-stone-200 text-stone-900' },
  scifi:   { label: 'Sci-Fi',   tone: 'bg-indigo-100 text-indigo-900' },
  sweet:   { label: 'Sweet',    tone: 'bg-amber-100 text-amber-900' },
};

const STATUS_TONE = {
  submitted:  'bg-amber-100 text-amber-900',
  paid:       'bg-emerald-100 text-emerald-900',
  in_review:  'bg-sky-100 text-sky-900',
  completed:  'bg-slate-200 text-slate-800',
  cancelled:  'bg-rose-100 text-rose-800',
};

export default function QADashboardPage() {
  const [assignments, setAssignments] = useState([]);
  const [loomRows,    setLoomRows]    = useState([]);
  const [metrics,     setMetrics]     = useState(null);
  const [busy,        setBusy]        = useState(false);
  const [error,       setError]       = useState('');

  const refresh = useCallback(async () => {
    setBusy(true); setError('');
    try {
      const [a, l, m] = await Promise.all([
        ax.get('/editing/editor/assignments').catch(() => ({ data: { assignments: [] } })),
        ax.get('/loom/editor-inbox').catch(() => ({ data: { manuscripts: [] } })),
        ax.get('/editing/editor/metrics').catch(() => ({ data: null })),
      ]);
      setAssignments(a.data?.assignments || []);
      setLoomRows(l.data?.manuscripts || []);
      setMetrics(m.data || null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="mx-auto max-w-5xl p-6" data-testid="qa-dashboard">
      <PanelHeader
        title="QA Reviewer Dashboard"
        subtitle="Incoming writer requests + Loom manuscripts ready for a quality pass."
        right={
          <Button size="sm" variant="outline" onClick={refresh}
                   data-testid="qa-refresh-btn" disabled={busy}>
            {busy
              ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              : <RefreshCw className="mr-1 h-3.5 w-3.5" />}
            Refresh
          </Button>
        }
      />
      {error && (
        <div className="mb-3 rounded-md border border-red-200 bg-red-50 p-3 text-[12.5px] text-red-800"
              data-testid="qa-error">
          {error}
        </div>
      )}

      {/* Metrics strip */}
      {metrics && (
        <div className="mb-6 grid grid-cols-3 gap-3" data-testid="qa-metrics-strip">
          <div className="rounded-md border border-border bg-card p-3"
                data-testid="qa-metric-open">
            <div className="text-[10.5px] uppercase tracking-[0.24em] text-muted-foreground">Open</div>
            <div className="mt-1 font-serif text-[22px]">{metrics.open_count ?? 0}</div>
            <div className="text-[11px] text-muted-foreground">Assigned · not yet completed</div>
          </div>
          <div className="rounded-md border border-border bg-card p-3"
                data-testid="qa-metric-week">
            <div className="text-[10.5px] uppercase tracking-[0.24em] text-muted-foreground">This week</div>
            <div className="mt-1 font-serif text-[22px]">{metrics.reviews_this_week ?? 0}</div>
            <div className="text-[11px] text-muted-foreground">Reviews completed · last 7 days</div>
          </div>
          <div className="rounded-md border border-border bg-card p-3"
                data-testid="qa-metric-turnaround">
            <div className="text-[10.5px] uppercase tracking-[0.24em] text-muted-foreground">Avg turnaround</div>
            <div className="mt-1 font-serif text-[22px]">
              {metrics.avg_turnaround_h != null ? `${metrics.avg_turnaround_h} h` : '—'}
            </div>
            <div className="text-[11px] text-muted-foreground">Assigned → completed · last 30 days</div>
          </div>
        </div>
      )}

      {/* Writer QA requests */}
      <section className="mb-8" data-testid="qa-writer-requests-section">
        <div className="mb-2 flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-muted-foreground">
          <ClipboardList className="h-3.5 w-3.5" />
          Writer QA Requests
          <Badge variant="outline" className="text-[10px]">{assignments.length}</Badge>
        </div>
        {busy && !assignments.length && (
          <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
          </div>
        )}
        {!busy && !assignments.length && (
          <div className="rounded-md border border-dashed p-6 text-center text-[12px] text-muted-foreground"
                data-testid="qa-writer-requests-empty">
            No incoming writer QA requests right now. When a writer pays for an editorial pass, the request lands here.
          </div>
        )}
        <div className="space-y-2">
          {assignments.map((r) => (
            <div key={r.id}
                  className="rounded-md border border-border bg-card p-3 text-[12.5px]"
                  data-testid={`qa-writer-row-${r.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <ClipboardList className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="font-serif text-[13.5px]">
                      {r.project_name || 'Untitled project'}
                    </span>
                    <Badge className={`text-[10px] uppercase ${STATUS_TONE[r.status] || ''}`}
                            data-testid={`qa-writer-status-${r.id}`}>
                      {r.status}
                    </Badge>
                    {r.tier_label && (
                      <Badge variant="outline" className="text-[10px]">{r.tier_label}</Badge>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-3 text-[11.5px] text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      {r.author_email || 'unknown@'}
                    </span>
                    {r.project_author && <span>· {r.project_author}</span>}
                    {r.assigned_at && (
                      <span>· assigned {new Date(r.assigned_at).toLocaleString()}</span>
                    )}
                    {r.word_count != null && (
                      <span>· {Number(r.word_count).toLocaleString()} words</span>
                    )}
                  </div>
                </div>
                <Button size="sm" variant="outline" asChild
                         data-testid={`qa-writer-open-${r.id}`}>
                  <a href={`/qa/review/${r.id}`}>Open manuscript</a>
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Loom manuscript inbox */}
      <section data-testid="qa-loom-inbox-section">
        <div className="mb-2 flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-muted-foreground">
          <BookOpen className="h-3.5 w-3.5" />
          Loom Manuscripts · Ready for QA
          <Badge variant="outline" className="text-[10px]">{loomRows.length}</Badge>
        </div>
        {!busy && !loomRows.length && (
          <div className="rounded-md border border-dashed p-6 text-center text-[12px] text-muted-foreground"
                data-testid="qa-loom-empty">
            No Loom manuscripts currently in the QA queue.
          </div>
        )}
        <div className="space-y-2">
          {loomRows.map((m) => (
            <div key={m.id}
                  className="rounded-md border border-border bg-card p-3 text-[12.5px]"
                  data-testid={`qa-loom-row-${m.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <BookOpen className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="font-serif text-[13.5px]">{m.spec_title}</span>
                    <Badge className={`text-[10px] uppercase ${BUCKET_META[m.spec_bucket]?.tone || ''}`}>
                      {BUCKET_META[m.spec_bucket]?.label || m.spec_bucket}
                    </Badge>
                    <Badge variant="outline" className="text-[10px]">{m.status}</Badge>
                    <span className="text-[11px] text-muted-foreground">
                      by <b>{m.pseudonym_name || m.pseudonym_id}</b>
                    </span>
                  </div>
                  <div className="mt-1 text-[11.5px] text-muted-foreground">
                    Progress: {m.chapters_drafted ?? 0} / {m.chapters_total ?? '?'} chapters drafted
                  </div>
                </div>
                <Button size="sm" variant="outline" asChild
                         data-testid={`qa-loom-open-${m.id}`}>
                  <a href={`/editor/${m.project_id}`}>Open manuscript</a>
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="mt-8 text-center text-[11px] text-muted-foreground">
        Signed in as QA · <Link to="/" className="underline hover:opacity-80">Home</Link>
      </div>
    </div>
  );
}
